import React, { useState, useRef, useEffect, useLayoutEffect } from 'react'
import { BiFolder, BiChevronRight } from 'react-icons/bi'
import { getFolderItemCount, formatItemCount } from './folderService'
import { isMobile } from '../../../utils/deviceInfo'

// Cascading submenu ("Add to folder ›" / "Move to folder ›") that lives inside
// the row 3-dots menu. Hovering (desktop) or tapping (mobile) the trigger opens
// a flyout listing every folder; clicking one adds/moves the item immediately
// (no modal). A "+ Create new folder…" row sits at the bottom. The flyout opens
// to the LEFT of the menu, falling back to the right near the viewport edge.
//
// Props:
//   mode            'add' | 'move'
//   folders         folder[]
//   membership      itemId -> folderId map (for the per-folder counts)
//   currentFolderId in move mode, this folder renders greyed + non-clickable
//   memberFolderIds the folder(s) the item is in (0 or 1 under the move model) —
//                   each renders greyed + non-clickable (can't add into it again)
//   onSelect(id)    a folder was chosen
//   onCreateNew()   the "+ Create new folder…" row was chosen
//   leadingIcon     optional node rendered before the label, so the trigger can
//                   match menus whose other entries are icon-indented (the row
//                   3-dots menus pass nothing and are unchanged)

// Approx. flyout width incl. gap — kept in sync with .folder-submenu-panel CSS.
const FLYOUT_WIDTH = 248

const FolderSubmenu = ({
  mode,
  folders,
  membership,
  currentFolderId,
  memberFolderIds,
  itemNoun,
  onSelect,
  onCreateNew,
  leadingIcon,
}) => {
  const [open, setOpen] = useState(false)
  const [placeRight, setPlaceRight] = useState(false)
  const [placeUp, setPlaceUp] = useState(false)
  const containerRef = useRef(null)
  const flyoutRef = useRef(null)

  const label = mode === 'move' ? 'Move to folder' : 'Add to folder'
  const list = folders || []

  // Decide left vs right placement based on room to the left of the trigger.
  useLayoutEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      setPlaceRight(rect.left < FLYOUT_WIDTH + 8)
      // Open upward when the flyout would run past the bottom of the viewport
      // (rows near the bottom of the list otherwise clip the folder list).
      const flyoutH = flyoutRef.current ? flyoutRef.current.offsetHeight : 0
      setPlaceUp(rect.top + flyoutH > window.innerHeight - 8)
    }
  }, [open])

  // Close when clicking/tapping outside the submenu.
  useEffect(() => {
    if (!open) {
      return undefined
    }
    const handleOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutside)
    return () => {
      document.removeEventListener('mousedown', handleOutside)
    }
  }, [open])

  const handleSelect = (folderId) => {
    setOpen(false)
    if (onSelect) {
      onSelect(folderId)
    }
  }

  const handleCreateNew = () => {
    setOpen(false)
    if (onCreateNew) {
      onCreateNew()
    }
  }

  const renderFolder = (folder) => {
    const isCurrent = currentFolderId && folder._id === currentFolderId
    // A folder the item already belongs to can't be added/moved into again.
    const isMember =
      memberFolderIds && memberFolderIds.indexOf(folder._id) !== -1
    const isDisabled = isCurrent || isMember
    const count = formatItemCount(
      getFolderItemCount(membership, folder._id),
      itemNoun
    )
    if (isDisabled) {
      return (
        <div
          key={folder._id}
          className="folder-submenu-item folder-submenu-item-current">
          <span className="folder-row-icon-wrap folder-submenu-icon-wrap">
            <BiFolder className="folder-row-icon" />
          </span>
          <span className="folder-submenu-item-name">{folder.name}</span>
          <span className="folder-submenu-item-count">{count}</span>
        </div>
      )
    }
    return (
      <div
        key={folder._id}
        className="folder-submenu-item cursor-pointer"
        onClick={() => handleSelect(folder._id)}>
        <span className="folder-row-icon-wrap folder-submenu-icon-wrap">
          <BiFolder className="folder-row-icon" />
        </span>
        <span className="folder-submenu-item-name">{folder.name}</span>
        <span className="folder-submenu-item-count">{count}</span>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className="folder-submenu"
      onMouseEnter={isMobile ? undefined : () => setOpen(true)}
      onMouseLeave={isMobile ? undefined : () => setOpen(false)}>
      <div
        className="dropdown-item my-1 cursor-pointer folder-submenu-trigger"
        onClick={() => setOpen((prev) => !prev)}>
        {leadingIcon}
        <span className="folder-submenu-trigger-label">{label}</span>
        <BiChevronRight className="folder-submenu-chevron" />
      </div>

      {open && (
        <div
          ref={flyoutRef}
          className={`folder-submenu-flyout ${
            placeRight
              ? 'folder-submenu-flyout-right'
              : 'folder-submenu-flyout-left'
          } ${placeUp ? 'folder-submenu-flyout-up' : ''}`}>
          <div className="folder-submenu-panel">
            {list.map((folder) => renderFolder(folder))}
            <div
              className="folder-submenu-create cursor-pointer"
              onClick={handleCreateNew}>
              + Create new folder…
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FolderSubmenu
